import { Link, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '~/lib/api';
import { authClient } from '~/lib/auth-client';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { IconButton } from './ui/icon-button';

const APP_NAME = import.meta.env.VITE_APP_NAME ?? '{{APP_NAME}}';

const tierLabel: Record<string, string> = {
  foundation: 'Foundation',
  optimization: 'Optimization',
  transformation: 'Transformation',
};

const NAV: Array<{ to: string; label: string; founderOnly?: boolean }> = [
  { to: '/today', label: 'Today' },
  { to: '/coach', label: 'Coach' },
  { to: '/practices', label: 'Practices' },
  { to: '/history', label: 'History' },
  { to: '/profile', label: 'Profile' },
  { to: '/biometrics', label: 'Biometrics' },
  { to: '/community', label: 'Community' },
  { to: '/founder', label: 'Founder', founderOnly: true },
];

export function Header() {
  const { data } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const user = data?.user ?? null;
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const visibleNav = NAV.filter((n) => !n.founderOnly || user?.personaMode === 'founder');

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center gap-6 px-4 sm:px-6 h-14">
        <Link to="/" className="font-serif text-lg leading-none tracking-tight shrink-0">
          {APP_NAME}
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {visibleNav.map((n) => {
              const active =
                location.pathname === n.to || location.pathname.startsWith(`${n.to}/`);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`px-2.5 py-1.5 rounded-[var(--radius-sm)] transition-colors ${
                    active
                      ? 'text-foreground bg-muted'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <>
              <Badge variant="outline" className="hidden sm:inline-flex">
                {tierLabel[user.tier] ?? user.tier}
              </Badge>
              <div className="relative" ref={menuRef}>
                <IconButton label="account" onClick={() => setMenuOpen((v) => !v)}>
                  <UserCircleIcon />
                </IconButton>
                {menuOpen && (
                  <div className="absolute right-0 mt-1 w-56 rounded-[var(--radius)] border border-border bg-background shadow-md p-1 animate-rise">
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border mb-1">
                      {user.email}
                    </div>
                    <MenuLink to="/settings" onClick={() => setMenuOpen(false)}>
                      Settings
                    </MenuLink>
                    <MenuLink to="/billing" onClick={() => setMenuOpen(false)}>
                      Billing
                    </MenuLink>
                    <button
                      onClick={async () => {
                        await authClient.signOut();
                        window.location.href = '/';
                      }}
                      className="w-full text-left rounded-[var(--radius-sm)] px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link to="/login">
              <Button size="sm">Sign in</Button>
            </Link>
          )}
        </div>
      </div>

      {user && (
        <nav className="md:hidden border-t border-border overflow-x-auto">
          <div className="flex items-center gap-1 px-3 py-2 text-[13px]">
            {visibleNav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`shrink-0 px-3 py-1.5 rounded-full transition-colors ${
                    active
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </header>
  );
}

function MenuLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block rounded-[var(--radius-sm)] px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {children}
    </Link>
  );
}

function UserCircleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3" />
      <path d="M5.5 19c1.5-2.5 4-4 6.5-4s5 1.5 6.5 4" />
    </svg>
  );
}
